/* eslint-disable no-console */
import { useEffect } from 'react';

interface HeadingValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  h1Count: number;
  headingOutline: Array<{ index: number; tag: string; text: string; className: string }>;
}

export const validateHeadingHierarchy = (container: Document | HTMLElement = document): HeadingValidationResult => {
  const h1s = container.querySelectorAll('h1');
  const allHeadings = container.querySelectorAll('h1, h2, h3, h4, h5, h6');

  const errors: string[] = [];
  const warnings: string[] = [];

  // Check h1 count
  if (h1s.length === 0) {
    errors.push('NO H1 FOUND - Every page needs exactly one h1');
  } else if (h1s.length > 1) {
    errors.push(`MULTIPLE H1s FOUND (${h1s.length}) - Should be exactly one`);
    h1s.forEach((h, i) => {
      warnings.push(`  ${i + 1}. "${h.textContent?.trim().substring(0, 50)}"`);
    });
  }

  // Check for skipped levels
  const levels = Array.from(allHeadings).map((h) => parseInt(h.tagName[1]));

  for (let i = 1; i < levels.length; i++) {
    const prev = levels[i - 1];
    const curr = levels[i];

    if (curr > prev + 1) {
      const heading = allHeadings[i];
      const missingLevels = [];
      for (let level = prev + 1; level < curr; level++) {
        missingLevels.push(`h${level}`);
      }
      const missingText = missingLevels.length === 1 ? missingLevels[0] : missingLevels.join(', ');
      warnings.push(
        `Missing ${missingText}: Found h${curr} "${heading.textContent
          ?.trim()
          .substring(0, 40)}" directly after h${prev}`,
      );
    }
  }

  const headingOutline = Array.from(allHeadings).map((h, i) => ({
    index: i + 1,
    tag: h.tagName,
    text: h.textContent?.trim().substring(0, 60) || '',
    className: h.className.substring(0, 40),
  }));

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    h1Count: h1s.length,
    headingOutline,
  };
};

/**
 * Validates heading hierarchy and logs results to console
 * @param container - Optional DOM element to scope validation (e.g., a dialog/modal). Defaults to entire document.
 * @param isDialog - Set to true when validating a dialog/modal (different H1 rules apply)
 */
export const validateHeadings = (container?: HTMLElement, isDialog = false): void => {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') {
    return;
  }

  const result = validateHeadingHierarchy(container || document);

  const scopeLabel = container ? (isDialog ? '(dialog/modal validation)' : '(scoped validation)') : '';
  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log(`HEADING HIERARCHY REPORT ${scopeLabel}`);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

  // Show h1 status (different rules for dialogs vs pages)
  if (isDialog) {
    // Dialog-specific validation: H1 should NOT exist in dialogs
    if (result.h1Count > 0) {
      console.log(`❌ DIALOG SHOULD NOT HAVE H1 - Found ${result.h1Count} h1(s)`);
      console.log('   Dialog titles should be h2 or h3 (page already has h1)');
    } else {
      console.log('✅ No h1 in dialog (correct - page context has h1)');
    }

    // Check if dialog has ANY headings (it should!)
    if (result.headingOutline.length === 0) {
      console.log('⚠️  WARNING: Dialog has no headings');
      console.log('   Dialog titles should be semantic headings (h2 or h3) for accessibility');
      console.log('   Found aria-labelledby:', (container as HTMLElement)?.getAttribute('aria-labelledby'));
    } else {
      const firstHeading = result.headingOutline[0];
      console.log(`✅ Dialog has ${result.headingOutline.length} heading(s), starting with ${firstHeading.tag}`);
    }
  } else {
    // Page validation: Exactly one H1 required
    if (result.h1Count === 0) {
      console.log('❌ NO H1 FOUND - Every page needs exactly one h1');
    } else if (result.h1Count > 1) {
      console.log(`❌ MULTIPLE H1s FOUND (${result.h1Count}) - Should be exactly one`);
      result.warnings.forEach((warn) => console.warn(warn));
    } else {
      const h1Element = (container || document).querySelector('h1');
      console.log(`✅ Exactly one h1: "${h1Element?.textContent?.trim().substring(0, 50)}"`);
    }
  }

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');

  // Show level skip warnings
  const skipWarnings = result.warnings.filter((w) => w.includes('Missing'));
  if (skipWarnings.length > 0) {
    console.log(`⚠️  Found ${skipWarnings.length} skipped level(s):\n`);
    skipWarnings.forEach((warn) => console.log(`   ${warn}`));
  } else {
    console.log('✅ No skipped heading levels');
  }

  console.log('\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  // COMPLETE HEADING OUTLINE (temporarily disabled to reduce console noise)
  // console.log('COMPLETE HEADING OUTLINE:\n');
  // console.table(result.headingOutline);
  // console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  // Determine validity based on context (dialog vs page)
  let relevantErrors = result.errors;
  let totalWarnings = result.warnings.length;

  if (isDialog) {
    // For dialogs, ignore H1 count errors (0 H1s is correct)
    relevantErrors = result.errors.filter((e) => !e.includes('NO H1 FOUND'));

    // Flag warning if dialog has no headings
    if (result.headingOutline.length === 0) {
      totalWarnings++;
    }
  }

  // Critical errors (H1 issues)
  const hasCriticalErrors = relevantErrors.length > 0;
  const hasWarnings = totalWarnings > 0;

  if (!hasCriticalErrors && !hasWarnings) {
    console.log('✅ RESULT: Heading hierarchy is valid');
    if (isDialog) {
      console.log('   - No h1 in dialog (correct)');
      console.log('   - No skipped levels');
      console.log('   - Proper nesting structure');
    } else {
      console.log('   - Exactly 1 h1');
      console.log('   - No skipped levels');
      console.log('   - Proper nesting structure');
    }
  } else if (hasCriticalErrors) {
    console.log('❌ RESULT: Heading hierarchy has CRITICAL ERRORS');
    console.log(`   - Found ${relevantErrors.length} critical error(s) (H1 issues)`);
    if (hasWarnings) {
      console.log(`   - Also found ${totalWarnings} warning(s)`);
    }
    console.log('   - See details above');
  } else if (hasWarnings) {
    console.log('⚠️  RESULT: Heading hierarchy has warnings');
    console.log('   - No critical errors (H1 is correct)');
    console.log(`   - Found ${totalWarnings} warning(s) - accessibility improvements recommended`);
    console.log('   - See warnings above');
  }
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
};

export const useHeadingValidation = (): void => {
  useEffect(() => {
    if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') {
      return;
    }

    const timeoutId = setTimeout(validateHeadings, 100);
    return () => clearTimeout(timeoutId);
  }, []);
};

// Checks that page has H1 and dialog doesn't
export const validatePageWithDialog = (dialogElement: HTMLElement): void => {
  console.log('🔍 Validating full context (page + dialog)...\n');

  // Check page has H1
  console.log('📄 PAGE CONTEXT:');
  const pageH1Count = document.querySelectorAll('h1').length;
  if (pageH1Count === 0) {
    console.log('❌ Page has no H1');
  } else if (pageH1Count > 1) {
    console.log(`⚠️  Page has ${pageH1Count} H1s (should be 1)`);
  } else {
    console.log(`✅ Page has exactly 1 H1: "${document.querySelector('h1')?.textContent?.trim().substring(0, 50)}"`);
  }

  // Check dialog doesn't have H1
  console.log('\n💬 DIALOG CONTEXT:');
  validateHeadings(dialogElement, true);
};

// Expose to window for manual validation in dev console
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).validateHeadings = validateHeadings;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).validatePageWithDialog = validatePageWithDialog;
}
